import type { Metadata } from "next";
import { ANDROID_PACKAGE_NAME } from "@/lib/native/app-links";
import { OFFICIAL_APP_URL } from "@/lib/public-app-url";

export const SITE_NAME = "Hiive";
export const DEFAULT_SITE_DESCRIPTION =
  "Discover local hiives, events, fitness sessions, venue experiences, and people around sports, movement, and wellness.";

// app.hiive.club, not the hiive.club apex: the apex redirects to the marketing
// site (www.hiive.club), so an apex canonical would point every page at a 307
// off-site. OFFICIAL_APP_URL in lib/public-app-url.ts must stay in sync.
const FALLBACK_SITE_URL = OFFICIAL_APP_URL;
const FALLBACK_OG_IMAGE = "/android-chrome-512x512.png";

export const indexableRobots: Metadata["robots"] = {
  index: true,
  follow: true,
  googleBot: {
    index: true,
    follow: true,
    "max-image-preview": "large",
    "max-snippet": -1,
    "max-video-preview": -1,
  },
};

export const noIndexRobots: Metadata["robots"] = {
  index: false,
  follow: true,
  googleBot: {
    index: false,
    follow: true,
    "max-image-preview": "large",
    "max-snippet": -1,
    "max-video-preview": -1,
  },
};

/**
 * Search Console / Bing verification tokens.
 *
 * These render as `<meta name="google-site-verification">`. The DNS TXT method
 * also works, but the meta tag survives a nameserver change, so both are worth
 * having. NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION was set for months without
 * anything reading it, so the property was never verified.
 */
export function verificationMetadata(): Metadata["verification"] {
  const google = process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION?.trim();
  const bing = process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION?.trim();

  if (!google && !bing) return undefined;

  return {
    ...(google ? { google } : {}),
    ...(bing ? { other: { "msvalidate.01": bing } } : {}),
  };
}

export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;

/**
 * Numeric App Store id (the `id123456789` in the listing URL). It only exists
 * once the app has been created in App Store Connect, so every consumer treats
 * it as optional — an `apple-itunes-app` banner pointing at a nonexistent id
 * renders a dead banner, which is worse than no banner.
 */
export function appStoreId() {
  const value = process.env.NEXT_PUBLIC_IOS_APP_STORE_ID?.trim();
  return value && /^\d+$/.test(value) ? value : undefined;
}

export function appStoreUrl() {
  const id = appStoreId();
  return id ? `https://apps.apple.com/app/id${id}` : undefined;
}

/**
 * Native app-link metadata for pages whose whole purpose is to hand off to the
 * app: an invite, a session join, a coupon.
 *
 * - `appLinks` emits the `al:*` tags Facebook, Messenger, WhatsApp and X read to
 *   open the app directly instead of their in-app browser.
 * - `itunes` emits `apple-itunes-app`, the Safari Smart App Banner. Safari also
 *   uses `app-argument` to pass the exact URL through to the app, so a tap
 *   lands on the invite rather than the home screen.
 *
 * Only attach this to claimed App Link paths (src/lib/native/app-links.ts).
 * Putting it on indexable content pages buries the page under a banner.
 */
export function nativeAppLinkMetadata(path: string): Metadata {
  const url = absoluteUrl(path);
  const iosAppStoreId = appStoreId();

  return {
    appLinks: {
      android: {
        package: ANDROID_PACKAGE_NAME,
        url,
        app_name: SITE_NAME,
      },
      ios: {
        url,
        app_name: SITE_NAME,
        ...(iosAppStoreId ? { app_store_id: iosAppStoreId } : {}),
      },
    },
    ...(iosAppStoreId
      ? { itunes: { appId: iosAppStoreId, appArgument: url } }
      : {}),
  };
}

export function getSiteUrl() {
  const configured = process.env.NEXT_PUBLIC_APP_URL?.trim();
  const candidate =
    configured && configured.length > 0 ? configured : FALLBACK_SITE_URL;

  try {
    const url = new URL(candidate);
    return url.origin;
  } catch {
    return FALLBACK_SITE_URL;
  }
}

export function absoluteUrl(path = "/") {
  if (/^https?:\/\//i.test(path)) return path;
  return new URL(
    path.startsWith("/") ? path : `/${path}`,
    getSiteUrl(),
  ).toString();
}

export function cleanText(value: string | null | undefined, fallback = "") {
  const text = (value ?? fallback).replace(/\s+/g, " ").trim();
  return text.length > 0 ? text : fallback;
}

export function truncateMeta(value: string, maxLength = 158) {
  const text = cleanText(value);
  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, maxLength - 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, lastSpace > 80 ? lastSpace : clipped.length).trim()}...`;
}

/**
 * Extensions no social crawler will render as a preview image.
 *
 * WhatsApp, X, Facebook, LinkedIn and iMessage all want a still. Handed a
 * video they show no image at all — not a frame, not a fallback — so a shared
 * link previews as bare text. Hiive event and venue banners are very often
 * mp4s, which is exactly how the most-shared pages on the site ended up with
 * the worst previews on it.
 */
const NON_IMAGE_MEDIA = /\.(mp4|mov|m4v|webm|avi|mkv|m3u8|mpd)(\?|#|$)/i;

/**
 * The last gate before a URL becomes `og:image`.
 *
 * Callers are expected to resolve a poster themselves (`firstMediaPreviewUrl`
 * over `firstMediaUrl`), and this exists because "expected to" is not a
 * guarantee across two dozen `generateMetadata` functions. A video that slips
 * through falls back to the site icon: a generic preview is worth more than
 * none.
 */
export function socialImageUrl(image?: string | null): string {
  const candidate = image?.trim();
  if (!candidate || NON_IMAGE_MEDIA.test(candidate)) {
    return absoluteUrl(FALLBACK_OG_IMAGE);
  }
  return absoluteUrl(candidate);
}

/**
 * Anything a caller can hand to `buildPageMetadata` as the share image.
 *
 * The string form is the historical one and stays supported; the object form
 * carries the dimensions of the exact derivative being published, which is what
 * lets a crawler commit to a large card before the bytes arrive.
 */
export type MetadataImage = { url: string; width?: number; height?: number };

/**
 * `og:locale`. English content aimed at an Indian audience: the territory is
 * not decoration, it is the signal that stops a crawler defaulting the page to
 * en_US and, with it, to a US result set.
 */
const OG_LOCALE = "en_IN";

function imageMimeType(url: string) {
  const extension = new URL(url, getSiteUrl()).pathname
    .split(".")
    .pop()
    ?.toLowerCase();
  switch (extension) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    case "gif":
      return "image/gif";
    default:
      return undefined;
  }
}

export function buildPageMetadata({
  title,
  description,
  path,
  image,
  imageAlt,
  type = "website",
  robots = indexableRobots,
}: {
  title: string;
  description: string;
  path: string;
  image?: MetadataImage | string | null;
  /**
   * Alt text for the share image. Defaults to the page title, which is right
   * for a banner that depicts the thing the page is about and wrong for
   * nothing — but a caller that knows better (a person's photo, a venue's
   * room) should say so.
   */
  imageAlt?: string | null;
  type?: "website" | "article" | "profile";
  robots?: Metadata["robots"];
}): Metadata {
  const metaDescription = truncateMeta(description);
  const canonical = absoluteUrl(path);
  const candidate = typeof image === "string" ? { url: image } : image;
  const imageUrl = socialImageUrl(candidate?.url);
  const isFallbackImage = imageUrl === absoluteUrl(FALLBACK_OG_IMAGE);
  // Dimensions describe the URL that was asked for. The moment `socialImageUrl`
  // swaps in the site icon — because the caller resolved a video, or nothing —
  // the caller's numbers describe a different file, so they go with it.
  const measured = isFallbackImage
    ? { width: 512, height: 512 }
    : candidate?.width && candidate.height
      ? { width: candidate.width, height: candidate.height }
      : {};
  const alt = cleanText(imageAlt ?? undefined) || title;
  const mimeType = imageMimeType(imageUrl);

  return {
    title,
    description: metaDescription,
    alternates: {
      canonical,
    },
    robots,
    openGraph: {
      type,
      siteName: SITE_NAME,
      locale: OG_LOCALE,
      title,
      description: metaDescription,
      url: canonical,
      images: [
        {
          url: imageUrl,
          // Only ever the dimensions of the file at this exact URL. Declaring
          // 512x512 over a real 1920x1080 banner made every crawler that
          // trusts the declaration crop the card to a square.
          ...measured,
          ...(mimeType ? { type: mimeType } : {}),
          alt,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description: metaDescription,
      images: [{ url: imageUrl, alt }],
    },
  };
}

function safeJsonLd(data: unknown) {
  // Safari's built-in structured-data reader calls @context.toLowerCase on
  // the root. A valid JSON-LD array triggers a browser error on every visit.
  // A graph object keeps all nodes while providing a root context.
  const document = Array.isArray(data)
    ? { "@context": "https://schema.org", "@graph": data }
    : data;
  return JSON.stringify(document).replace(/</g, "\\u003c");
}

export function JsonLd({ data }: { data: unknown }) {
  return (
    <script
      type="application/ld+json"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD is serialized server-side with '<' escaped.
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  );
}

export function breadcrumbJsonLd(
  items: Array<{ name: string; path?: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.path ? { item: absoluteUrl(item.path) } : {}),
    })),
  };
}

export function webPageJsonLd({
  path,
  name,
  description,
  type = "WebPage",
  image,
}: {
  path: string;
  name: string;
  description: string;
  type?: string;
  image?: string | null;
}) {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description: truncateMeta(description, 300),
    isPartOf: {
      "@id": `${absoluteUrl("/")}#website`,
    },
    ...(image ? { image: absoluteUrl(image) } : {}),
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${absoluteUrl("/")}#organization`,
    name: SITE_NAME,
    url: absoluteUrl("/"),
    logo: absoluteUrl("/android-chrome-512x512.png"),
    description: DEFAULT_SITE_DESCRIPTION,
  };
}

/**
 * MobileApplication entities for the iOS and Android builds, linked back to the
 * Organization. This is how Google associates app.hiive.club with the two store
 * listings, which feeds the app pack in mobile search results and lets Search
 * Console's "App" reporting attach to this property.
 *
 * The iOS entry is omitted until NEXT_PUBLIC_IOS_APP_STORE_ID exists — a
 * SoftwareApplication with no downloadUrl is dropped as invalid anyway.
 */
export function mobileApplicationsJsonLd() {
  const iosUrl = appStoreUrl();
  const publisher = { "@id": `${absoluteUrl("/")}#organization` };
  const shared = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    applicationCategory: "SportsApplication",
    description: DEFAULT_SITE_DESCRIPTION,
    publisher,
    // The stores are free to install; bookings are paid separately.
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
  } as const;

  return [
    {
      ...shared,
      "@id": `${absoluteUrl("/")}#android-app`,
      name: `${SITE_NAME} for Android`,
      operatingSystem: "ANDROID",
      installUrl: PLAY_STORE_URL,
      downloadUrl: PLAY_STORE_URL,
    },
    ...(iosUrl
      ? [
          {
            ...shared,
            "@id": `${absoluteUrl("/")}#ios-app`,
            name: `${SITE_NAME} for iOS`,
            operatingSystem: "IOS",
            installUrl: iosUrl,
            downloadUrl: iosUrl,
          },
        ]
      : []),
  ];
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${absoluteUrl("/")}#website`,
    name: SITE_NAME,
    url: absoluteUrl("/"),
    publisher: {
      "@id": `${absoluteUrl("/")}#organization`,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${absoluteUrl("/discover")}?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function itemListJsonLd({
  path,
  name,
  items,
}: {
  path: string;
  name: string;
  items: Array<{ name: string; path: string; image?: string | null }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${absoluteUrl(path)}#itemlist`,
    name,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: absoluteUrl(item.path),
      name: item.name,
      ...(item.image ? { image: absoluteUrl(item.image) } : {}),
    })),
  };
}

/**
 * A `PostalAddress` node, or nothing.
 *
 * Schema.org permits a bare string for `address`, and Google's local rich
 * results quietly do not: an unparsed string is read as a free-text label, so a
 * venue that typed its full address got none of the local treatment that the
 * same address inside a `PostalAddress` earns. The event page already wrapped
 * its address this way; the venue and hiive pages did not, which is the only
 * reason two nodes describing the same street were graded differently.
 *
 * Nothing here parses the string into components — that guesses, and a wrong
 * `addressLocality` is worse than an absent one. `addressCountry` is asserted
 * because every Hiive address is Indian and Google weights the country field
 * heavily for local intent; a string that already names another country is left
 * alone rather than contradicted.
 */
export function postalAddressJsonLd(address: string | null | undefined) {
  const streetAddress = cleanText(address);
  if (!streetAddress) return undefined;

  const looksForeign =
    /\b(usa|united states|uk|united kingdom|canada|australia|singapore|uae|dubai)\b/i.test(
      streetAddress,
    );

  return {
    "@type": "PostalAddress",
    streetAddress,
    ...(looksForeign ? {} : { addressCountry: "IN" }),
  };
}

/**
 * An `AggregateRating` node, or nothing.
 *
 * Google drops the whole review snippet — and sometimes the enclosing entity's
 * rich result with it — when `ratingValue` is zero or `reviewCount` is zero, so
 * an unrated venue must publish no rating rather than a rating of none.
 */
export function aggregateRatingJsonLd(
  summary: { averageRating: number; publishedCount: number } | null | undefined,
) {
  if (!summary) return undefined;
  const { averageRating, publishedCount } = summary;
  if (!Number.isFinite(averageRating) || averageRating <= 0) return undefined;
  if (!Number.isFinite(publishedCount) || publishedCount < 1) return undefined;

  return {
    "@type": "AggregateRating",
    ratingValue: Number(averageRating.toFixed(1)),
    bestRating: 5,
    worstRating: 1,
    reviewCount: publishedCount,
  };
}

/**
 * `ImageObject` nodes for a page's gallery.
 *
 * The page's single `image` is what a share card uses; this is what Google
 * Images indexes. A crawler that only ever sees one image per page has one
 * candidate per page to rank, which is how a venue with a dozen photos ends up
 * represented by its banner alone.
 */
export function imageObjectsJsonLd(
  images: Array<{ url: string; width?: number; height?: number; alt?: string }>,
) {
  const nodes = images
    .filter((image) => image.url && !NON_IMAGE_MEDIA.test(image.url))
    .slice(0, 12)
    .map((image) => ({
      "@type": "ImageObject" as const,
      url: absoluteUrl(image.url),
      contentUrl: absoluteUrl(image.url),
      ...(image.width ? { width: image.width } : {}),
      ...(image.height ? { height: image.height } : {}),
      ...(image.alt ? { caption: image.alt } : {}),
    }));

  return nodes.length > 0 ? nodes : undefined;
}
